import { Paper, TableCell, TableRow, Tooltip, Typography, makeStyles } from '@material-ui/core';
import countries from 'i18n-iso-countries';
import { isEmpty } from 'lodash';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconType, getIcon } from '../modules/data';
import {
  EquipmentHighlight,
  PackageDetails,
  SourceRegisterPotentialDTO,
  ValuationCountryCode,
} from '../modules/generated/api';
import { bodyTypes as bodyTypeOptions, getLabel, sources } from '../modules/labels';

import CarDetailTable from './CarDetailTable';
import CarDetailTableHeadCell from './CarDetailTableHeadCell';
import CarDetailTableUnitValueCell from './CarDetailTableUnitValueCell';
import HighlightIcon from './HighlightIcon';
import PackageDetailPotentials from './PackageDetailPotentials';
import { PromotionIcon } from './PromotionIcon';
import UnitValue from './UnitValue';

type PackageDetailBasicsProps = {
  packageDetails: PackageDetails;
  comparisonCountries: Set<ValuationCountryCode>;
  setComparisonCountries: (countries: Set<ValuationCountryCode>) => void;
  countryPotentials: { [key: string]: SourceRegisterPotentialDTO };
  equipmentKeys: EquipmentHighlight[];
};

const useStyles = makeStyles(() => ({
  header: {
    padding: '6px 24px 6px 16px',
  },
  table: {
    boxShadow: 'none',
    '& th': {
      width: '25%',
    },
  },
  highlights: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
}));

const PackageDetailBasics = ({
  packageDetails,
  comparisonCountries,
  setComparisonCountries,
  countryPotentials,
  equipmentKeys,
}: PackageDetailBasicsProps) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const {
    packageId,
    source,
    pricePackage,
    countries: packageCountries,
    promotions,
    bodyTypes: bodyTypesMap,
    batteryCapacityGross,
  } = packageDetails;
  const promotionKeys = promotions ? Object.keys(promotions).filter((p) => p !== 'VVJ') : [];

  return (
    <Paper>
      <Typography variant="h2" className={classes.header}>
        {t('common.package')}
      </Typography>
      <CarDetailTable title="" disableHeader disableElevation className={classes.table}>
        <TableRow>
          <CarDetailTableHeadCell>
            <div>{`${t('common.package')}-ID`}</div>
            {source && <div>{t('common.source')}</div>}
            {!isEmpty(packageCountries) && <div>{t('servicePacketDrawer.form.country.label')}</div>}
          </CarDetailTableHeadCell>
          <TableCell>
            <div>{packageId}</div>
            {source && <div>{getLabel(sources, source)}</div>}
            {!isEmpty(packageCountries) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginTop: 2 }}>
                {Object.entries(packageCountries!).map(([countryCode, count]) => (
                  <Tooltip
                    key={countryCode}
                    title={`${countries.getName(countryCode, i18n.language) ?? countryCode} (${count})`}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <img width={14} src={getIcon(IconType.COUNTRY, countryCode)} alt="" />
                      <span>{count}</span>
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
          </TableCell>
        </TableRow>

        {!isEmpty(bodyTypesMap) && (
          <TableRow>
            <CarDetailTableHeadCell>{t('car.bodyType')}</CarDetailTableHeadCell>
            <TableCell>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                {Object.entries(bodyTypesMap!)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => (
                    <Tooltip key={type} title={`${getLabel(bodyTypeOptions, type)} (${count})`}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span>{getLabel(bodyTypeOptions, type)}</span>
                        <span>({count})</span>
                      </span>
                    </Tooltip>
                  ))}
              </div>
            </TableCell>
          </TableRow>
        )}

        {batteryCapacityGross && (
          <TableRow>
            <CarDetailTableHeadCell>{t('car.batteryCapacityGross')}</CarDetailTableHeadCell>
            <TableCell>
              {batteryCapacityGross.min != null ? `${batteryCapacityGross.min} kWh` : '-'}
              {' – '}
              {batteryCapacityGross.max != null ? `${batteryCapacityGross.max} kWh` : '-'}
              {batteryCapacityGross.average != null && ` (∅ ${Math.round(batteryCapacityGross.average)} kWh)`}
            </TableCell>
          </TableRow>
        )}

        {equipmentKeys.length > 0 && (
          <TableRow>
            <CarDetailTableHeadCell>{t('common.sharedEquipment')}</CarDetailTableHeadCell>
            <TableCell>
              <div className={classes.highlights}>
                {equipmentKeys.map((equipment: EquipmentHighlight) => (
                  <HighlightIcon key={equipment} type={IconType.EQUIPMENT} value={equipment} />
                ))}
              </div>
            </TableCell>
          </TableRow>
        )}

        {promotionKeys.length > 0 && (
          <TableRow>
            <CarDetailTableHeadCell>{t('common.sharedPromotions')}</CarDetailTableHeadCell>
            <TableCell>
              <div className={classes.highlights}>
                {promotionKeys.map((promotion) => (
                  <PromotionIcon
                    key={promotion}
                    promotion={promotion}
                    highlight={['STELLPLATZ', 'AFTERSHOW'].includes(promotion)}
                  />
                ))}
              </div>
            </TableCell>
          </TableRow>
        )}

        {pricePackage && (
          <TableRow>
            <CarDetailTableHeadCell>{t('car.totalPrice')}</CarDetailTableHeadCell>
            <CarDetailTableUnitValueCell>
              <UnitValue value={pricePackage.value} unit={pricePackage.unit || 'EUR'} />
            </CarDetailTableUnitValueCell>
          </TableRow>
        )}

        <PackageDetailPotentials
          comparisonCountries={comparisonCountries}
          setCheckedCountries={setComparisonCountries}
          countryPotentials={countryPotentials}
        />
      </CarDetailTable>
    </Paper>
  );
};

export default memo(PackageDetailBasics);
